import {
  AdditiveBlending,
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  Group,
  Points,
  PointsMaterial,
} from 'three/webgpu';
import { type AudioFrame } from '../core/audio-frame.js';
import { Gentle } from '../core/gentle.js';
import { createRandom } from '../core/random.js';
import { Smoothed } from '../core/smoothed.js';

const MOTES_PER_SHELL = 2600;
const INNER_RADIUS = 70;
const OUTER_RADIUS = 210;
const MOTE_COLORS = [0x88_aa_ff, 0xff_ff_ff, 0xff_cc_aa, 0xcc_dd_ff, 0xff_aa_cc] as const;

interface Shell {
  readonly points: Points;
  readonly material: PointsMaterial;
}

function createShell(seed: number, size: number): Shell {
  const random = createRandom(seed);
  const positions: number[] = [];
  const colors: number[] = [];
  const color = new Color();

  for (let i = 0; i < MOTES_PER_SHELL; i += 1) {
    // A thick spherical shell far outside the action: it parallaxes as the
    // camera orbits and gives the eye something to measure distance against.
    const radius = INNER_RADIUS + (OUTER_RADIUS - INNER_RADIUS) * Math.cbrt(random());
    const cosTheta = random() * 2 - 1;
    const sinTheta = Math.sqrt(Math.max(0, 1 - cosTheta * cosTheta));
    const phi = random() * Math.PI * 2;
    positions.push(
      Math.cos(phi) * sinTheta * radius,
      cosTheta * radius * 0.7,
      Math.sin(phi) * sinTheta * radius,
    );
    color.setHex(MOTE_COLORS[Math.floor(random() * MOTE_COLORS.length)] ?? 0xff_ff_ff);
    colors.push(color.r, color.g, color.b);
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));

  const material = new PointsMaterial({
    size,
    vertexColors: true,
    transparent: true,
    opacity: 0.4,
    blending: AdditiveBlending,
    depthWrite: false,
    sizeAttenuation: true,
  });

  return { points: new Points(geometry, material), material };
}

/**
 * Two counter-rotating shells of distant motes. They turn slowly and change
 * brightness only through a rate limiter, so they read as depth rather than as
 * sparkle — the old version flickered its opacity on every beat, which is
 * exactly the kind of thing the eye reports as flashing.
 */
export class StarField {
  public readonly group = new Group();
  private readonly outer = createShell(0xbe_ef, 0.55);
  private readonly inner = createShell(0xca_fe, 0.4);
  private readonly treble = new Smoothed(0.4);
  private readonly outerFade = new Gentle(1, 0.12, 0.4);
  private readonly innerFade = new Gentle(1.2, 0.1, 0.3);

  constructor() {
    this.group.add(this.outer.points, this.inner.points);
  }

  public update(frame: AudioFrame, deltaSeconds: number): void {
    const treble = this.treble.update(frame.treble, deltaSeconds);

    this.outer.points.rotation.y += deltaSeconds * 0.006;
    this.inner.points.rotation.y -= deltaSeconds * 0.0045;
    this.inner.points.rotation.x += deltaSeconds * 0.002;

    this.outer.material.opacity = this.outerFade.update(0.3 + treble * 0.25, deltaSeconds);
    this.inner.material.opacity = this.innerFade.update(0.22 + frame.level * 0.18, deltaSeconds);
  }
}
